// src/products/dataroom/hooks/useGroupManagement.js
// Custom hook for managing group data and API operations

import { useState, useCallback, useMemo } from 'react';
import RoleService from '../../../services/roleService';

const useGroupManagement = (projectId, userRole, onMessage) => {
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Check if user can manage groups
  const canManageGroups = RoleService.hasAdminAccess(userRole?.toLowerCase());

  // Dummy users for testing (could be moved to a separate service later)
  const dummyUsers = useMemo(() => [
    { id: 'dummy-1', email: 'john.doe@investor.com', name: 'John Doe', role: 'Investor' },
    { id: 'dummy-2', email: 'jane.smith@lawyer.com', name: 'Jane Smith', role: 'Legal' },
    { id: 'dummy-3', email: 'mike.johnson@consultant.com', name: 'Mike Johnson', role: 'Consultant' },
    { id: 'dummy-4', email: 'sarah.wilson@company.com', name: 'Sarah Wilson', role: 'Employee' },
    { id: 'dummy-5', email: 'david.brown@director.com', name: 'David Brown', role: 'Director' },
    { id: 'dummy-6', email: 'lisa.garcia@advisor.com', name: 'Lisa Garcia', role: 'Advisor' },
    { id: 'dummy-7', email: 'alex.chen@partner.com', name: 'Alex Chen', role: 'Partner' },
    { id: 'dummy-8', email: 'emily.davis@analyst.com', name: 'Emily Davis', role: 'Analyst' },
    { id: 'dummy-9', email: 'robert.martinez@manager.com', name: 'Robert Martinez', role: 'Manager' },
    { id: 'dummy-10', email: 'anna.lee@specialist.com', name: 'Anna Lee', role: 'Specialist' }
  ], []);

  // Fetch groups from API
  const fetchGroups = useCallback(async () => {
    if (!projectId) {
      setLoading(false);
      return;
    }
    
    try {
      setLoading(true);
      
      const response = await fetch(`/api/dataroom/groups/${projectId}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`HTTP error! status: ${response.status} - ${errorText}`);
      }

      const result = await response.json();
      
      if (result.success) {
        // Transform the data to include member count
        const transformedGroups = (result.data || []).map(group => ({
          ...group,
          dataroom_user_groups: Array.from({ length: group.member_count || 0 }, (_, i) => ({ id: i }))
        }));
        
        setGroups(transformedGroups);
      } else {
        throw new Error(result.error || 'Failed to fetch groups');
      }
    } catch (error) {
      console.error('Error fetching groups:', error);
      onMessage && onMessage('Failed to load groups: ' + error.message, 'error');
    } finally {
      setLoading(false);
    }
  }, [projectId, onMessage]);

  // Create new group
  const createGroup = useCallback(async (groupData) => {
    if (!groupData.name?.trim()) {
      onMessage && onMessage('Please enter a group name', 'error');
      return false;
    }

    if (!canManageGroups) {
      onMessage && onMessage('Permission Denied: Only Super Admin, Creator, or Support can create groups.', 'error');
      return false;
    }

    try {
      const response = await fetch('/api/dataroom/groups', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          name: groupData.name,
          description: groupData.description,
          project_id: projectId
        }),
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`HTTP error! status: ${response.status} - ${errorText}`);
      }

      const result = await response.json();
      
      if (result.success) {
        await fetchGroups(); // Refresh the list
        onMessage && onMessage('Group created successfully!', 'success');
        return true;
      } else {
        throw new Error(result.error || 'Failed to create group');
      }
    } catch (error) {
      console.error('Error creating group:', error);
      onMessage && onMessage('Failed to create group: ' + error.message, 'error');
      return false;
    }
  }, [projectId, canManageGroups, fetchGroups, onMessage]);

  // Update group
  const updateGroup = useCallback(async (groupId, groupData) => {
    if (!groupData.name?.trim()) {
      onMessage && onMessage('Please enter a group name', 'error');
      return false;
    }

    if (!canManageGroups) {
      onMessage && onMessage('Permission Denied: Only Super Admin, Creator, or Support can update groups.', 'error');
      return false;
    }

    try {
      const response = await fetch(`/api/dataroom/groups/${groupId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          name: groupData.name,
          description: groupData.description
        }),
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`HTTP error! status: ${response.status} - ${errorText}`);
      }

      const result = await response.json();
      
      if (result.success) {
        await fetchGroups(); // Refresh the list
        onMessage && onMessage('Group updated successfully!', 'success');
        return true;
      } else {
        throw new Error(result.error || 'Failed to update group');
      }
    } catch (error) {
      console.error('Error updating group:', error);
      onMessage && onMessage('Failed to update group: ' + error.message, 'error');
      return false;
    }
  }, [canManageGroups, fetchGroups, onMessage]);

  // Delete group
  const deleteGroup = useCallback(async (groupId) => {
    if (!canManageGroups) {
      onMessage && onMessage('Permission Denied: Only Super Admin, Creator, or Support can delete groups.', 'error');
      return false;
    }

    try {
      const response = await fetch(`/api/dataroom/groups/${groupId}`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`HTTP error! status: ${response.status} - ${errorText}`);
      }

      const result = await response.json();
      
      if (result.success) {
        await fetchGroups(); // Refresh the list
        onMessage && onMessage('Group deleted successfully!', 'success');
        return true;
      } else {
        throw new Error(result.error || 'Failed to delete group');
      }
    } catch (error) {
      console.error('Error deleting group:', error);
      onMessage && onMessage('Failed to delete group: ' + error.message, 'error');
      return false;
    }
  }, [canManageGroups, fetchGroups, onMessage]);

  // Add user to group
  const addUserToGroup = useCallback(async (groupId, userEmail) => {
    if (!userEmail?.trim()) {
      onMessage && onMessage('Please enter a user email', 'error');
      return false;
    }
    
    if (!canManageGroups) {
      onMessage && onMessage('Permission Denied: Only Super Admin, Creator, or Support can add users to groups.', 'error');
      return false;
    }

    // For testing, find user in dummy data
    const foundUser = dummyUsers.find(u => u.email.toLowerCase() === userEmail.toLowerCase());
    if (!foundUser) {
      onMessage && onMessage('User not found in the system', 'error');
      return false;
    }

    try {
      const response = await fetch(`/api/dataroom/groups/${groupId}/users`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          user_id: foundUser.id,
          project_id: projectId
        }),
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`HTTP error! status: ${response.status} - ${errorText}`);
      }

      const result = await response.json();
      
      if (result.success) {
        // Refresh groups to show updated member count
        await fetchGroups();
        onMessage && onMessage(`Added ${foundUser.name} to the group!`, 'success');
        return true;
      } else {
        throw new Error(result.error || 'Failed to add user to group');
      }
    } catch (error) {
      console.error('Error adding user to group:', error);
      onMessage && onMessage('Failed to add user to group: ' + error.message, 'error');
      return false;
    }
  }, [projectId, canManageGroups, fetchGroups, dummyUsers, onMessage]);

  // Remove user from group
  const removeUserFromGroup = useCallback(async (groupId, userId) => {
    if (!canManageGroups) {
      onMessage && onMessage('Permission Denied: Only Super Admin, Creator, or Support can remove users from groups.', 'error');
      return false;
    }
    
    try {
      const response = await fetch(`/api/dataroom/groups/${groupId}/users/${userId}`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`HTTP error! status: ${response.status} - ${errorText}`);
      }

      const result = await response.json();
      
      if (result.success) {
        // Refresh groups to show updated member count
        await fetchGroups();
        onMessage && onMessage('User removed from group!', 'success');
        return true;
      } else {
        throw new Error(result.error || 'Failed to remove user from group');
      }
    } catch (error) {
      console.error('Error removing user from group:', error);
      onMessage && onMessage('Failed to remove user from group: ' + error.message, 'error');
      return false;
    }
  }, [canManageGroups, fetchGroups, onMessage]);

  // Get group users (using dummy data)
  const getGroupUsers = useCallback((group) => {
    const userIds = group.dataroom_user_groups?.map(ug => ug.user_id) || [];
    return dummyUsers.filter(user => userIds.includes(user.id));
  }, [dummyUsers]);

  return {
    // State
    groups,
    loading,
    canManageGroups,
    dummyUsers,
    
    // Actions
    fetchGroups,
    createGroup,
    updateGroup,
    deleteGroup,
    addUserToGroup,
    removeUserFromGroup,
    getGroupUsers
  };
};

export default useGroupManagement;
